import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

export const Books = ({ scrollToId }) => {
  const [books, setBooks] = useState([]);

  useEffect(() => {
    api
      .get("/books")
      .then((r) => setBooks(r.data))
      .catch(() => setBooks([]));
  }, []);

  return (
    <section
      id="books"
      data-testid="books-section"
      className="relative py-24 sm:py-32 bg-parchment-alt dark:bg-[#0B0F19]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <Overline>Books & Published Works</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Words written in service of hope.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Each title is an invitation — to reflect, to serve, and to carry the
              work forward.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1}>
              <motion.article
                data-testid={`book-card-${b.id}`}
                whileHover={{ y: -8, rotateX: 4, rotateY: -4 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                style={{ transformStyle: "preserve-3d", perspective: 1000 }}
                className="group h-full rounded-2xl border border-border bg-card overflow-hidden flex flex-col"
              >
                <div
                  className="relative h-56 flex flex-col justify-end p-6"
                  style={{
                    background: `linear-gradient(145deg, ${b.accent} 0%, #0F172A 130%)`,
                  }}
                >
                  <span className="absolute top-5 left-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
                    {b.category}
                  </span>
                  <PenLine className="absolute top-5 right-5 text-white/40" size={20} />
                  <h3 className="font-serif text-2xl font-semibold text-white leading-tight">
                    {b.title}
                  </h3>
                  <p className="mt-1 text-sm text-white/75">{b.subtitle}</p>
                  <span className="mt-3 font-mono text-xs text-white/60">{b.year}</span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {b.description}
                  </p>
                  <div className="mt-5 pl-4 border-l-2 border-gold/60 text-sm italic text-foreground/80">
                    <Quote size={14} className="inline mr-1 text-gold-deep dark:text-gold-light" />
                    {b.excerpt}
                  </div>
                  <button
                    data-testid={`book-request-${b.id}`}
                    onClick={() => scrollToId("contact")}
                    className="mt-6 self-start text-sm font-semibold text-gold-deep dark:text-gold-light hover:underline underline-offset-4"
                  >
                    Request a signed copy →
                  </button>
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
